/**
 * Texto de compartilhamento, montado **no cliente**.
 *
 * A regra que manda aqui é a mesma da partida: **as palavras nunca aparecem**. O que
 * viaja é a forma do resultado — quantas letras foram pagas em cada palavra, o placar, o
 * modo e o dia. Quem lê o texto não ganha vantagem nenhuma na chain de hoje.
 */

const CERTA = '🟩';
const PAGA = '🟨';
const PERDIDA = '🟥';

/**
 * Uma linha por palavra: uma casa amarela por letra revelada além da que vem de graça,
 * e a última casa diz se a palavra caiu ou não.
 */
export function grade(words = []) {
  return words.map((word) => {
    const pagas = Math.max(0, (word.revealed_at_solve || 1) - 1);
    return PAGA.repeat(pagas) + (word.solved ? CERTA : PERDIDA);
  });
}

/** Data no formato curto de quem lê em português. */
function day(date) {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(date);
}

/**
 * Monta o texto pronto para colar.
 *
 * `url` entra como última linha quando existe — o convite de volta é o que faz o
 * compartilhamento trazer alguém, e não só contar vantagem.
 */
export function buildText({ summary, modeName = 'Sentido', date = new Date(), url = '' }) {
  const didWin = summary.state === 'won';
  const header = `Sentido · ${modeName} · ${day(date)}`;
  const board = `${summary.score} pontos${didWin ? '' : ' · a chain parou antes'}`;

  return [header, board, '', ...grade(summary.words), url].filter((row) => row !== null).join('\n').trim();
}

/**
 * Cópia sem a Clipboard API.
 *
 * `navigator.clipboard` só existe em contexto seguro: `https` ou `localhost`. Abrir o
 * jogo pelo IP da máquina na rede local — que é como se testa no celular — cai fora
 * disso, e sem este caminho o botão de copiar simplesmente não faz nada.
 */
function copiarNoBraco(text) {
  const campo = document.createElement('textarea');
  campo.value = text;
  campo.setAttribute('readonly', '');
  campo.style.cssText = 'position:fixed;top:-1000px;opacity:0';
  document.body.appendChild(campo);
  campo.select();
  let deu = false;
  try {
    deu = document.execCommand('copy');
  } catch {
    deu = false;
  }
  document.body.removeChild(campo);
  return deu;
}

/**
 * Entrega o texto pelo caminho que o aparelho tiver.
 *
 * Devolve `'shared'`, `'copied'`, `'canceled'` ou `'failed'` — quem chamou decide o que
 * dizer na tela.
 *
 * **No dedo, compartilhar; no mouse, copiar.** A Web Share API existe no Chrome de
 * desktop e abre a folha do Windows, que não é o que quem está no computador quer: ele
 * quer o texto no Ctrl+C. Por isso a folha do sistema só entra em ponteiro grosso, e no
 * desktop a cópia é o caminho normal, não o plano B.
 */
export async function share(text) {
  const noDedo = window.matchMedia?.('(pointer: coarse)')?.matches;

  if (noDedo && navigator.share) {
    try {
      await navigator.share({ text: text });
      return 'shared';
    } catch (error) {
      // Cancelar a folha do sistema não é falha: o jogador desistiu, e cair na cópia
      // colocaria o texto na área de transferência sem ele pedir.
      if (error?.name === 'AbortError') return 'canceled';
    }
  }

  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return copiarNoBraco(text) ? 'copied' : 'failed';
  }
}
